import React, { 
  useRef, 
  useState, 
  useEffect, 
  FC, 
} from 'react';
import { Link } from 'react-router-dom';
import useCustomTranslation from '@/hooks/useCustomTranslation';
import {
  Space,
  Button,
  Drawer,
  Cascader,
  Typography,
  Row,
  Col,
  Divider,
  Badge,
  Popover,
  CascaderProps,
} from 'antd';
import socket from '@/socket';
import useAuth from '@/hooks/useAuth';
import { groupWarehouses } from '@/shared/helpers/groupWarehouses';
import useModal from '@/hooks/useModal';
import isScreenMobile from '@/shared/helpers/isScreenMobile';
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  SwapOutlined,
  BellOutlined,
  DownOutlined,
  UpOutlined,
} from '@ant-design/icons';
import useBreakpoints from '@/hooks/useBreakpoint';
import usePaginationService from '@/hooks/usePaginationService';
import ENotificationType from '@/enums/ENotificationType';
import { getNotificationsPaginated } from '@/services/NotificationsService';
import formatOrderId from '@/shared/helpers/Order/formatOrderId';
import formatEntryPlanId from '@/shared/helpers/FormatEntryPlanId';
import formatSolpedId from '@/shared/helpers/formatSolpedId';
import formatSupplyOrderId from '@/shared/helpers/formatSupplyOrderId';
import formatDeliveryId from '@/shared/helpers/Delivery/formatDelivery';
import INotification, { 
  IDeliveryCreatedPayload, 
  IEntryPlanCreatedPayload, 
  IInventoryAlertPayload, 
  IOrderCreatedPayload, 
  ISolpedCreatedPayload, 
  ISupplyOrderCreatedPayload,
  ISupplyOrderFlagPayload, 
} from '@/interfaces/INotification';
import { getMasterFranchiseConfigurations } from '@/services/MasterFranchiseConfigurationService';
import TopbarProfile from './TopbarProfile';
import MyPersonalRackPicking from './MyPersonalRackPicking';
import NotificationsList from './NotificationsList';
import CurrencyExchangeDisplay from './CurrencyExchangeDisplay';
import NotificationListItem, { INotificationItem } from './NotificationListItem';

interface ITopbar {
  collapsed: boolean;
  onClickCollapsed: () => void;
  menuToggleLabel?: string;
}

interface ICascaderOption {
  value: string;
  label: string;
  children?: ICascaderOption[];
  disableCheckbox?: boolean;
}

const Topbar: FC<ITopbar> = ({
  collapsed,
  onClickCollapsed,
  menuToggleLabel,
}) => {
  const { t } = useCustomTranslation();
  const auth = useAuth();
  const myPersonalRackModal = useModal();

  const { isBreakpointEqualsAndBelow } = useBreakpoints();
  const isMobile = isBreakpointEqualsAndBelow('sm');
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const [warehouseContexText, setWarehouseContexText] = useState(t('g.loading'));

  // calulate warehouses context text
  useEffect(() => {
    if (!auth) return;

    const selectedWhs = auth.selectedWarehouses.length;
    const totalWhs = auth.relatedWarehouses.length;
    // get warehouse text
    let contextWarehouseText = '';
    if (selectedWhs === totalWhs) {
      contextWarehouseText = t('g.all');
    } else {
      contextWarehouseText = `${selectedWhs}/${totalWhs} ${t('g.selected_warehouses').toLocaleLowerCase()}`;
    }
    setWarehouseContexText(contextWarehouseText);
  }, [auth?.relatedWarehouses, auth?.selectedWarehouses]);

  const warehouseContextOptions = Object.values(groupWarehouses(auth?.relatedWarehouses || []))
    .map(operative => ({
      label: operative.alias,
      value: operative.id.toString(),
      children: operative.warehouses.map(warehouse => ({
        label: warehouse.alias,
        value: warehouse.id.toString(),
      })),
    }));

  const handleWarehouseContextChange: CascaderProps<ICascaderOption, 'value', true>['onChange'] = (value) => {
    const warehouseIds: string[] = [];
    value.forEach((selectedCouple) => {
      const [_, warehouseId] = selectedCouple; // eslint-disable-line @typescript-eslint/no-unused-vars
      warehouseIds.push(warehouseId);
    });
    auth?.setSelectedWarehouses(warehouseIds);
  };

  const warehouseContextValues: Array<Array<string>> = [];
  auth?.selectedWarehouses.forEach((selectedWhId) => {
    const wh = auth.relatedWarehouses.find(relatedWh => relatedWh.id.toString() === selectedWhId.toString());
    if (wh) {
      warehouseContextValues.push([wh.operativeId.toString(), wh.id.toString()]);
    }
  });

  const selectAll = () => {
    auth?.setSelectedWarehouses(auth.relatedWarehouses.map(wh => wh.id.toString()));
  };
  const deselectAll = () => auth?.setSelectedWarehouses([]);

  const formatNotification = (notification: INotification): INotificationItem => {
    if (notification.type === ENotificationType.OrderCreated) {
      const { payload: { order, link } } = notification as INotification<IOrderCreatedPayload>;
      return {
        id: notification.id,
        title: `${t('g.order')} ${formatOrderId(order.id)}`,
        desc: 'Nuevo registro',
        // onClick: () => navigate(link),
        link,
      };
    }
    if (notification.type === ENotificationType.DeliveryCreated) {
      const { payload: { delivery, link } } = notification as INotification<IDeliveryCreatedPayload>;
      return {
        id: notification.id,
        title: `${t('g.delivery')} ${formatDeliveryId(delivery.id)}`,
        desc: 'Nuevo registro',
        // onClick: () => navigate(link),
        link,
      };
    }
    if (notification.type === ENotificationType.SolpedCreated) {
      const { payload: { solped, link } } = notification as INotification<ISolpedCreatedPayload>;
      return {
        id: notification.id,
        title: `${t('g.solped')} ${formatSolpedId(solped.id)}`,
        desc: 'Nuevo registro',
        // onClick: () => navigate(link),
        link,
      };
    }
    if (notification.type === ENotificationType.SupplyOrderCreated) {
      const { payload: { supplyOrder, link } } = notification as INotification<ISupplyOrderCreatedPayload>;
      return {
        id: notification.id,
        title: `${t('g.supply_order')} ${formatSupplyOrderId(supplyOrder.id)}`,
        desc: 'Nuevo registro',
        // onClick: () => navigate(link),
        link,
      };
    }
    if (notification.type === ENotificationType.EntryPlanCreated) {
      const { payload: { entryPlan, link } } = notification as INotification<IEntryPlanCreatedPayload>;
      return {
        id: notification.id,
        title: `${t('g.entry_plan')} ${formatEntryPlanId(entryPlan.id)}`,
        desc: 'Nuevo registro',
        // onClick: () => navigate(link),
        link,
      };
    }
    if (notification.type === ENotificationType.MaterialRequestExpiration) {
      const { payload: { materialRequestId, link } } = notification as INotification<{materialRequestId: number, link: string }>;
      return {
        id: notification.id,
        title: `${t('g.material_request')} #${materialRequestId}`,
        desc: 'Expirada',
        // onClick: () => navigate(link),
        link,
      };
    }
    if (notification.type === ENotificationType.MaterialRequestPreExpiration) {
      const { payload: { materialRequestId, link } } = notification as INotification<{materialRequestId: number, link: string }>;
      return {
        id: notification.id,
        title: `${t('g.material_request')} #${materialRequestId}`,
        desc: 'Proxima a expirar',
        // onClick: () => navigate(link),
        link,
      };
    }
    if (notification.type === ENotificationType.LowInventoryAlert) {
      const { payload: { warehouse, productName, productSku } } = notification as INotification<IInventoryAlertPayload>;
      return {
        id: notification.id,
        title: `Alerta de inventario: mínimo alcanzado`,
        desc: `El producto ${productName} (SKU: ${productSku}) ha alcanzado su inventario mínimo en ${warehouse}`,
        // onClick: () => navigate(link),
        link: '/inventory',
      };
    }
    if (notification.type === ENotificationType.HighInventoryAlert) {
      const { payload: { warehouse, productName, productSku } } = notification as INotification<IInventoryAlertPayload>;
      return {
        id: notification.id,
        title: `Alerta de inventario excedido`,
        desc: `El producto ${productName} (SKU: ${productSku}) ha alcanzado su inventario máximo en ${warehouse}`,
        // onClick: () => navigate(link),
        link: '/inventory',
      };
    }
    if (notification.type === ENotificationType.SupplyOrderFlag) {
      const { payload: { link, supplyOrderId } } = notification as INotification<ISupplyOrderFlagPayload>;
      return {
        id: notification.id,
        title: `${t('g.supply_order')} pendiente`,
        desc: `${t('g.supply_order')} ${formatSupplyOrderId(supplyOrderId)}`,
        // onClick: () => navigate(link),
        link: link,
      };
    }
    console.error('could not format notification', notification);
    return {
      id: notification.id,
      title: notification.type,
      desc: JSON.stringify(notification.payload),
    };
  };

  const [notifications, setNotifications] = useState<INotificationItem[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const notificationsState = usePaginationService({
    fetchPage: getNotificationsPaginated,
    currentPage: 1,
    pageSize: 10,
    params: {
      '$sort[createdAt]': -1,
    },
  });

  useEffect(() => {
    const newNotifications = notificationsState.data;
    // console.log('data change', newNotifications);

    setNotifications((prev) => {
      const prevIds = prev.map(p => p.id);
      // console.log({ prevIds });
      const updatedList = [...prev];
      newNotifications.forEach((notification) => {
        if (!prevIds.includes(notification.id)) {
          // console.log('new id', { id: notification.id });
          updatedList.push(formatNotification(notification));
        }
      });
      return updatedList;
    });
  }, [notificationsState.data]);
  
  const onLoadMore = () => {
    notificationsState.setCurrentPage((current: number) => current + 1);
  };

  const loadMore = !(notificationsState.loading || notifications.length >= notificationsState.total) ? (
    <Button style={{ marginTop: 8 }} onClick={onLoadMore} block>{t('g.load_more')}</Button>
  ) : null; 

  const onNotificationEvent = async (event: INotification) => {
    // prepend to current notifications list
    setNotificationCount(prev => prev + 1);
    setNotifications(prev => ([formatNotification(event), ...prev]));

    // play notification sound accordingly
    if (event.type === ENotificationType.OrderCreated) {
      playNotification(notificationAudioRefOrder);
    }
    if (event.type === ENotificationType.SolpedCreated) {
      playNotification(notificationAudioRefSolped);
    }
    if (event.type === ENotificationType.SupplyOrderCreated) {
      playNotification(notificationAudioRefSupplyOrder);
    }
  };

  useEffect(() => {
    const ordersSocketListeners = socket.service('notifications').listeners('created');

    if (ordersSocketListeners.length === 0) {
      socket.service('notifications').on('created', onNotificationEvent);
    }

    return () => {
      socket.removeAllListeners();
    };
  }, []);

  const emptyNotificationSounds = {
    orders: 'none',
    solpeds: 'none',
    supplyOrders: 'none',
  };
  const [notificationSounds, setNotificationSounds] = useState(emptyNotificationSounds);

  // Ref to hold the Audio object
  const notificationAudioRefOrder = useRef<HTMLAudioElement | null>(null);
  const notificationAudioRefSolped = useRef<HTMLAudioElement | null>(null);
  const notificationAudioRefSupplyOrder = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    getMasterFranchiseConfigurations({ masterFranchiseId: 1 })
      .then((masterConfigs) => {
        setNotificationSounds({
          orders: masterConfigs
            .find(config => config.key === 'notification_sound_on_order_created')?.value || 'none',
          solpeds: masterConfigs
            .find(config => config.key === 'notification_sound_on_solped_created')?.value || 'none',
          supplyOrders: masterConfigs
            .find(config => config.key === 'notification_sound_on_supply_order_created')?.value || 'none',
        });
      })
      .catch((error) => {
        console.error(error);
        setNotificationSounds(emptyNotificationSounds);
      });
  }, []);

  useEffect(() => {
    const unlockAudio = () => {
      unlockAudioRef(notificationAudioRefOrder);
      unlockAudioRef(notificationAudioRefSolped);
      unlockAudioRef(notificationAudioRefSupplyOrder);
      window.removeEventListener('click', unlockAudio);
    };
    
    window.addEventListener('click', unlockAudio);
  }, []);

  // Initialize audio once if notificationSound exists
  useEffect(() => {
    if (notificationSounds.orders !== 'none') {
      notificationAudioRefOrder.current = new Audio(notificationSounds.orders);
      notificationAudioRefOrder.current.load(); // Preload the audio
    }
    if (notificationSounds.solpeds !== 'none') {
      notificationAudioRefSolped.current = new Audio(notificationSounds.solpeds);
      notificationAudioRefSolped.current.load(); // Preload the audio
    }
    if (notificationSounds.supplyOrders !== 'none') {
      notificationAudioRefSupplyOrder.current = new Audio(notificationSounds.supplyOrders);
      notificationAudioRefSupplyOrder.current.load(); // Preload the audio
    }
  }, [notificationSounds.orders, notificationSounds.solpeds, notificationSounds.supplyOrders]);

  const unlockAudioRef = (notificationRef: React.MutableRefObject<HTMLAudioElement | null>) => {
    if (notificationRef.current) {
      notificationRef.current.volume = 0;
      notificationRef.current
        .play()
        .then(() => {
          notificationRef.current?.pause();
          notificationRef.current!.currentTime = 0;
          notificationRef.current!.volume = 1;
        })
        .catch((err) => { console.error(err); });
    }
  };

  // Function to play sound
  const playNotification = (notificationRef: React.MutableRefObject<HTMLAudioElement | null>) => {
    if (notificationRef.current) {
      notificationRef.current.currentTime = 0; // rewind to start
      notificationRef.current.play()
        .catch((err) => {
          console.error('Failed to play audio:', err);
        });
    }
  };

  const expandedControls = (
    <Space size="middle" align="center" wrap>
      <CurrencyExchangeDisplay />
      <Button
        type="primary"
        shape="circle"
        icon={<SwapOutlined className="align-icon" />}
        onClick={() => (myPersonalRackModal.show())}
      />
      <Cascader
        value={warehouseContextValues}
        options={warehouseContextOptions}
        onChange={handleWarehouseContextChange}
        showCheckedStrategy={Cascader.SHOW_CHILD}
        notFoundContent={t('g.results_not_found')}
        showSearch
        multiple
        popupRender={(menus: React.ReactNode) => (
          <Space direction="vertical">
            {menus}
            <Divider style={{ margin: 0 }} />
            <Button type="link" onClick={selectAll}>{t('g.select_all')}</Button>
            <Button type="link" onClick={deselectAll}>{t('g.deselect_all')}</Button>
          </Space>
        )}
      >
        <Typography.Text>
          {warehouseContexText} <Typography.Link>{t('g.change')}</Typography.Link>
        </Typography.Text>
      </Cascader>
      <Popover
        trigger="click"
        title={t('g.notifications')}
        content={(
          <NotificationsList
            loading={notificationsState.loading}
            loadMore={loadMore}
            dataSource={notifications}
            renderItem={(item) => (
              <NotificationListItem {...item} />
            )}
          />
        )}
        onOpenChange={(visible) => {
          if (visible) {
            setNotificationCount(0);
          }
        }}
      >
        <Badge count={notificationCount}>
          <Button
            type="text"
            icon={<BellOutlined className="align-icon" />}
          />
        </Badge>
      </Popover>
    </Space>
  );

  return (
    <div className="topbar__content">
      <Drawer
        open={myPersonalRackModal.visible}
        width="70%"
        title={t('g.move_products_in_inventory')}
        placement={isScreenMobile() ? 'bottom' : 'right'}
        height="90%"
        onClose={() => { myPersonalRackModal.hide(); }}
        styles={{
          body: {
            padding: isScreenMobile() ? 5 : undefined
          },
        }}
        destroyOnHidden
      >
        <MyPersonalRackPicking />
      </Drawer>
      <Row align="middle" justify="space-between">
        <Col>
          <Space>
            <Button
              type="text"
              size="large"
              icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={onClickCollapsed}
              aria-label={menuToggleLabel}
              aria-expanded={!collapsed}
              aria-controls="main-nav"
            />
            <Link className="topbar__logo" to="/" />
          </Space>
        </Col>
        <Col>
          <Space size="middle" align="center">
            {!isMobile && expandedControls}
            {isMobile && (
              <Button
                type="text"
                size="small"
                icon={mobileExpanded ? <UpOutlined /> : <DownOutlined />}
                onClick={() => setMobileExpanded(prev => !prev)}
              />
            )}
            <TopbarProfile />
          </Space>
        </Col>
      </Row>
      {isMobile && mobileExpanded && (
        <Row style={{ paddingTop: 8, paddingBottom: 4 }}>
          <Col span={24}>
            {expandedControls}
          </Col>
        </Row>
      )}
    </div>
  );
};

export default Topbar;
